/**
 * EarlyAccessSection Component
 * 
 * Full early access registration flow integrated into landing2.
 * Three paths: Sign In, Create Account, Continue as Guest
 * Dark theme matching the constellation background.
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import PathSelector from './PathSelector';
import './EarlyAccessSection.css';
import DarkAuthModal from './DarkAuthModal';
import DarkEarlyAccessForm from './DarkEarlyAccessForm';
import DarkConfirmationCard from './DarkConfirmationCard';

const EarlyAccessSection = () => {
  const { user } = useAuth();
  const [step, setStep] = useState('path-select'); // 'path-select', 'form', 'confirmation'
  const [authModal, setAuthModal] = useState({ open: false, mode: 'login' });
  const [submissionData, setSubmissionData] = useState(null);
  const sectionRef = useRef(null);

  // If user logs in, automatically go to form
  useEffect(() => {
    if (user && step === 'path-select') {
      setStep('form');
    }
  }, [user, step]);

  const handlePathSelect = (path) => {
    if (path === 'signin') {
      setAuthModal({ open: true, mode: 'login' });
    } else if (path === 'signup') {
      setAuthModal({ open: true, mode: 'signup' });
    } else if (path === 'guest') {
      setStep('form');
    }
  };

  const handleAuthClose = () => {
    setAuthModal({ open: false, mode: 'login' });
    // If user successfully authenticated, go to form
    if (user) {
      setStep('form');
    }
  };

  const handleFormConfirmation = (data) => {
    setSubmissionData(data);
    setStep('confirmation');
  };

  const handleBackToPath = () => {
    setStep('path-select');
  };

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: { duration: 0.3 },
    },
  };

  return (
    <section className="early-access-section" id="early-access" ref={sectionRef}>
      <motion.div
        className="early-access-section__container"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
        variants={sectionVariants}
      >
        {/* Section Header */}
        <div className="early-access-section__header">
          <span className="early-access-section__eyebrow">Early Access</span>
          <h2 className="early-access-section__title">
            Help Shape the Future
          </h2>
          <p className="early-access-section__subtitle">
            Whether you teach professionally, help friends learn, or share knowledge with your community —
            we're building this for you. Your feedback will directly influence what we create.
          </p>
        </div>

        {/* Dynamic Content Area */}
        <AnimatePresence mode="wait">
          {step === 'path-select' && !user && (
            <motion.div
              key="path-select"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <PathSelector onSelect={handlePathSelect} />
            </motion.div>
          )}

          {(step === 'form' || (step === 'path-select' && user)) && (
            <motion.div
              key="form"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <DarkEarlyAccessForm 
                onConfirmation={handleFormConfirmation}
                onBack={!user ? handleBackToPath : null}
              />
            </motion.div>
          )}

          {step === 'confirmation' && (
            <motion.div
              key="confirmation"
              variants={contentVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
                <DarkConfirmationCard submissionData={submissionData} onClose={() => setStep('path-select')} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Auth Modal */}
      <DarkAuthModal
        isOpen={authModal.open}
        onClose={handleAuthClose}
        initialMode={authModal.mode}
      />
    </section>
  );
};

export default EarlyAccessSection;
